// src/pages/pos/PosProductRefSync.js
import React, { useState, useEffect } from "react";
import {
    Row,
    Col,
    Card,
    Table,
    Badge,
    Spinner,
    Alert,
    Form,
    Button,
} from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import productAPI from "../../services/productapi";
import categoryAPI from "../../services/category";
import brandAPI from "../../services/brandapi";
import branchAPI from "../../services/branchapi";

const PosProductRefSync = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [items, setItems] = useState([]); // POS items + existing info
    const [summary, setSummary] = useState(null); // last save summary
    const [refCode, setRefCode] = useState(""); // user-typed ref code

    const [branches, setBranches] = useState([
        // You can replace this with API data
        { id: "all", name: "All Branches" },
    ]);
    const [selectedBranch, setSelectedBranch] = useState("all");

    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);

    const [savingIndex, setSavingIndex] = useState(null); // which row is being saved

    // 🔹 Load categories & brands on mount
    useEffect(() => {
        const initMasters = async () => {
            try {
                // 🔹 Categories
                const catRes = await categoryAPI.getAllCategory(10000, 0);
                const catItems = catRes?.data?.items || catRes?.data || [];
                setCategories(catItems);

                // 🔹 Brands
                const brandRes = await brandAPI.getAllBrands({
                    limit: 500,
                    page: 0,
                    status: 1,
                    sortModel: [],
                    filterModel: {},
                });
                const brandItems =
                    brandRes?.data?.result || brandRes?.data?.items || [];
                setBrands(brandItems);

                // 🔹 Branches (using existing branchAPI – NO changes in that file)
                const branchRes = await branchAPI.getAllBranches({status:1});
                // console.log(branchRes,"branchRes");
                // compatible with your sample: { status, message, data: { result: [...] } }
                const rawBranches =
                    branchRes?.data?.data?.result ||
                    branchRes?.data?.result ||
                    branchRes?.data ||
                    [];

                const formattedBranches = [
                    { id: "all", name: "All Branches" },
                    ...rawBranches.map((b) => ({
                        id: b.branchId, // we’ll send this to backend
                        name: `${b.branchName} (${b.code})`,
                        posId: b.posId,
                    })),
                ];

                setBranches(formattedBranches);
            } catch (err) {
                console.error("Error loading masters:", err);
                toast.error("Failed to load categories / brands / branches.");
            }
        };

        initMasters();
    }, []);


    // 🔹 Helper to get category/brand label
    const getCategoryName = (id) => {
        const c = categories.find((x) => x.categoryID === id || x.id === id);
        return c?.name || c?.categoryName || "";
    };

    const getBrandName = (id) => {
        const b = brands.find((x) => x.id === id || x.brandId === id);
        return b?.name || b?.brandName || "";
    };

    // 🔹 Handle field change for a row
    const handleItemFieldChange = (index, field, value) => {
        setItems((prev) =>
            prev.map((item, i) =>
                i === index
                    ? {
                        ...item,
                        [field]: value,
                    }
                    : item
            )
        );
    };

    // 1️⃣ Fetch from POS (optionally filtered by refCode + branch)
    const handleFetchFromPos = async () => {
        try {
            setIsLoading(true);
            setSummary(null);
            setItems([]);

            const trimmedRef = refCode.trim();

            const params = {};
            if (trimmedRef) params.refCode = trimmedRef;
            if (selectedBranch && selectedBranch !== "all") {
                params.branchId = Number(selectedBranch); // backend maps branchId → posId
            }

            const resData = await productAPI.fetchPosReferenceItems(params);

            if (resData.status === "SUCCESS") {
                const fetchedItems = resData.data?.items || [];

                // Pre-map editable fields (productName, categoryId, brandId)
                const mapped = fetchedItems.map((it) => ({
                    ...it,
                    // Ensure edit fields exist
                    editProductName: it.productName || "",
                    editCategoryId: it.categoryId || null,
                    editBrandId: it.brandId || null,
                }));

                if (!mapped.length) {
                    if (trimmedRef) {
                        toast.info(
                            `No POS items found for Ref Code: ${trimmedRef}`
                        );
                    } else {
                        toast.info("No POS items received from API.");
                    }
                } else {
                    toast.success(
                        resData.message || "POS items fetched successfully."
                    );
                }

                setItems(mapped);
            } else {
                toast.error(resData.message || "Failed to fetch POS items.");
                setItems([]);
            }
        } catch (err) {
            console.error("Fetch POS items error:", err);
            toast.error(
                err?.response?.data?.message ||
                err?.message ||
                "Something went wrong while fetching POS items."
            );
            setItems([]);
        } finally {
            setIsLoading(false);
        }
    };

    // 2️⃣ Save single row (create/update)
    const handleSaveRow = async (index) => {
        const item = items[index];
        if (!item) return;

        if (!item.editProductName) {
            toast.error("Product Name is required.");
            return;
        }

        if (!item.editCategoryId) {
            toast.error("Please select a Category.");
            return;
        }

        if (!item.editBrandId) {
            toast.error("Please select a Brand.");
            return;
        }

        try {
            setSavingIndex(index);
            setSummary(null);

            const payloadItem = {
                // required for sync
                itemRefCode: item.itemRefCode,
                productName: item.editProductName,
                mrp: item.mrp,
                sellingPrice: item.sellingPrice,
                stockQty: item.stockQty,
                existingProductId: item.existingProductId || null,
                // mapping
                categoryId: item.editCategoryId,
                brandId: item.editBrandId,
            };

            const resData = await productAPI.syncPosReferenceItems({
                items: [payloadItem],
            });

            if (resData.status === "SUCCESS") {
                toast.success(
                    resData.message || "Product synced successfully."
                );
                setSummary(resData.data || null);

                // Optionally, update row as "Linked" if it was new
                const updatedExistingId =
                    resData.data?.updatedProductId ||
                    payloadItem.existingProductId ||
                    item.existingProductId;

                setItems((prev) =>
                    prev.map((row, idx) =>
                        idx === index
                            ? {
                                ...row,
                                productName: payloadItem.productName,
                                existingProductId: updatedExistingId,
                                existingProductName: payloadItem.productName,
                                categoryId: payloadItem.categoryId,
                                brandId: payloadItem.brandId,
                            }
                            : row
                    )
                );
            } else {
                toast.error(resData.message || "Sync failed for this item.");
                setSummary(resData.data || null);
            }
        } catch (err) {
            console.error("Sync row error:", err);
            toast.error(
                err?.response?.data?.message ||
                err?.message ||
                "Something went wrong while syncing this product."
            );
        } finally {
            setSavingIndex(null);
        }
    };

    const renderItemsTable = () => {
        if (!items.length) return null;

        return (
            <Card className="mt-4">
                <Card.Header>
                    <strong>POS Items (by Reference Code)</strong>
                </Card.Header>
                <Card.Body>
                    <Table striped bordered hover size="sm" responsive>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Item Ref Code</th>
                                <th>POS Product Name</th>
                                <th>MRP</th>
                                <th>Selling Price</th>
                                <th>Stock Qty</th>
                                <th>Existing Product</th>
                                <th>Mapped Product Name</th>
                                <th>Category</th>
                                <th>Brand</th>
                                <th>Save</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, index) => {
                                const isExisting = !!item.existingProductId;
                                const catLabel = item.editCategoryId
                                    ? getCategoryName(item.editCategoryId)
                                    : "";
                                const brandLabel = item.editBrandId
                                    ? getBrandName(item.editBrandId)
                                    : "";

                                return (
                                    <tr key={item.itemRefCode || index}>
                                        <td>{index + 1}</td>
                                        <td>{item.itemRefCode}</td>
                                        <td>{item.productName}</td>
                                        <td>{item.mrp}</td>
                                        <td>{item.sellingPrice}</td>
                                        <td>{item.stockQty}</td>
                                        <td>
                                            {isExisting ? (
                                                <>
                                                    <Badge bg="secondary">
                                                        Linked
                                                    </Badge>
                                                    {item.existingProductName
                                                        ? ` – ${item.existingProductName}`
                                                        : ""}
                                                </>
                                            ) : (
                                                <Badge bg="success">
                                                    New
                                                </Badge>
                                            )}
                                        </td>

                                        {/* Editable Product Name */}
                                        <td>
                                            <Form.Control
                                                size="sm"
                                                type="text"
                                                value={item.editProductName || ""}
                                                onChange={(e) =>
                                                    handleItemFieldChange(
                                                        index,
                                                        "editProductName",
                                                        e.target.value
                                                    )
                                                }
                                            />
                                        </td>

                                        {/* Category Select */}
                                        <td>
                                            <Form.Select
                                                size="sm"
                                                value={
                                                    item.editCategoryId || ""
                                                }
                                                onChange={(e) =>
                                                    handleItemFieldChange(
                                                        index,
                                                        "editCategoryId",
                                                        e.target.value
                                                            ? Number(
                                                                e.target.value
                                                            )
                                                            : null
                                                    )
                                                }
                                            >
                                                <option value="">
                                                    Select Category
                                                </option>
                                                {categories.map((c) => (
                                                    <option
                                                        key={
                                                            c.categoryID ||
                                                            c.id
                                                        }
                                                        value={
                                                            c.categoryID ||
                                                            c.id
                                                        }
                                                    >
                                                        {c.name ||
                                                            c.categoryName}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </td>

                                        {/* Brand Select */}
                                        <td>
                                            <Form.Select
                                                size="sm"
                                                value={item.editBrandId || ""}
                                                onChange={(e) =>
                                                    handleItemFieldChange(
                                                        index,
                                                        "editBrandId",
                                                        e.target.value
                                                            ? Number(
                                                                e.target.value
                                                            )
                                                            : null
                                                    )
                                                }
                                            >
                                                <option value="">
                                                    Select Brand
                                                </option>
                                                {brands.map((b) => (
                                                    <option
                                                        key={b.id}
                                                        value={b.id}
                                                    >
                                                        {b.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </td>

                                        {/* Save Button */}
                                        <td className="text-center">
                                            <Button
                                                size="sm"
                                                variant="primary"
                                                onClick={() =>
                                                    handleSaveRow(index)
                                                }
                                                disabled={
                                                    savingIndex === index
                                                }
                                            >
                                                {savingIndex === index ? (
                                                    <>
                                                        <Spinner
                                                            as="span"
                                                            animation="border"
                                                            size="sm"
                                                            role="status"
                                                            aria-hidden="true"
                                                            className="me-1"
                                                        />
                                                        Saving...
                                                    </>
                                                ) : isExisting ? (
                                                    "Update"
                                                ) : (
                                                    "Create"
                                                )}
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        );
    };

    const renderSummary = () => {
        if (!summary) return null;

        const {
            totalItems,
            toCreate,
            toUpdate,
            createdCount,
            updatedCount,
            errors = [],
        } = summary;

        return (
            <Card className="mt-4">
                <Card.Header>
                    <strong>Last Sync Summary</strong>
                </Card.Header>
                <Card.Body>
                    <Row className="mb-3">
                        <Col md={3}>
                            <p>
                                <strong>Total Items:</strong>{" "}
                                {totalItems ?? "-"}
                            </p>
                        </Col>
                        <Col md={3}>
                            <p>
                                <strong>To Create:</strong>{" "}
                                <Badge bg="info">
                                    {toCreate ?? "-"}
                                </Badge>
                            </p>
                        </Col>
                        <Col md={3}>
                            <p>
                                <strong>To Update:</strong>{" "}
                                <Badge bg="warning">
                                    {toUpdate ?? "-"}
                                </Badge>
                            </p>
                        </Col>
                        <Col md={3}>
                            <p>
                                <strong>Created:</strong>{" "}
                                <Badge bg="success">
                                    {createdCount ?? "-"}
                                </Badge>{" "}
                                <strong>Updated:</strong>{" "}
                                <Badge bg="secondary">
                                    {updatedCount ?? "-"}
                                </Badge>
                            </p>
                        </Col>
                    </Row>

                    {errors.length > 0 && (
                        <>
                            <h6 className="mt-3 text-danger">Row Errors</h6>
                            <Table bordered size="sm" responsive>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Item Ref Code</th>
                                        <th>Product Name</th>
                                        <th>Error Message</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {errors.map((err, index) => (
                                        <tr key={index}>
                                            <td>{index + 1}</td>
                                            <td>{err.itemRefCode || "-"}</td>
                                            <td>{err.productName || "-"}</td>
                                            <td className="text-danger">
                                                {err.message || "Unknown error"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </>
                    )}

                    {!errors.length && (
                        <Alert variant="success" className="mt-3 mb-0">
                            All items synced successfully with no issues.
                        </Alert>
                    )}
                </Card.Body>
            </Card>
        );
    };

    return (
        <div className="container mt-4 mb-5">
            <ToastContainer />
            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <div>
                        <h4 className="mb-0">POS Product Reference Sync</h4>
                        <small className="text-muted">
                            Select branch & reference code, fetch from POS, then{" "}
                            <strong>
                                create / update mapping with Category & Brand
                            </strong>{" "}
                            in GSM.
                        </small>
                    </div>
                </Card.Header>

                <Card.Body>
                    <Row className="mb-3">
                        <Col md={4}>
                            {/* Branch select */}
                            <Form.Group controlId="branch">
                                <Form.Label>Branch</Form.Label>
                                <Form.Select
                                    value={selectedBranch}
                                    onChange={(e) => setSelectedBranch(e.target.value)}
                                    disabled={isLoading}
                                >
                                    {branches.map((b) => (
                                        <option key={b.id} value={b.id}>
                                            {b.name}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Text className="text-muted">
                                    Choose a specific branch or All Branches.
                                </Form.Text>
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            {/* Ref Code input */}
                            <Form.Group controlId="refCode">
                                <Form.Label>Item Ref Code (optional)</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter item ref code (RayMedi itemId)"
                                    value={refCode}
                                    onChange={(e) =>
                                        setRefCode(e.target.value)
                                    }
                                    disabled={isLoading}
                                />
                                <Form.Text className="text-muted">
                                    Leave empty to fetch all items for the
                                    branch.
                                </Form.Text>
                            </Form.Group>
                        </Col>

                        <Col
                            md={4}
                            className="d-flex justify-content-end align-items-end mt-3 mt-md-0"
                        >
                            <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={handleFetchFromPos}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        Fetching POS Items...
                                    </>
                                ) : (
                                    "Fetch from POS API"
                                )}
                            </button>
                        </Col>
                    </Row>

                    {renderItemsTable()}
                    {renderSummary()}
                </Card.Body>
            </Card>
        </div>
    );
};

export default PosProductRefSync;
